import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { HelpCircle, Search, ChevronLeft, BookOpen, MessageCircle, Mail, Send, Loader2, X, ChevronRight, Thermometer, ClipboardList, ShoppingCart, Package, Fuel, BarChart3, User, CheckSquare, Calendar, Wrench, CreditCard, Zap, Ticket, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MarkdownContent = ({ content }) => {
  if (!content) return null;
  const lines = content.split('\n');
  return (
    <div className="space-y-1.5 text-sm text-foreground">
      {lines.map((line, i) => {
        if (line.startsWith('### ')) return <h3 key={i} className="font-bold text-base mt-4 mb-1 text-primary">{line.slice(4)}</h3>;
        if (line.startsWith('## ')) return <h2 key={i} className="font-bold text-lg mt-4 mb-2">{line.slice(3)}</h2>;
        if (line.startsWith('> ')) return <div key={i} className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 px-3 py-2 rounded-r text-blue-800 dark:text-blue-300 text-xs mt-2">{line.slice(2)}</div>;
        if (line.startsWith('- ')) return <li key={i} className="ml-4 list-disc">{line.slice(2)}</li>;
        if (!line.trim()) return <div key={i} className="h-1" />;
        const parts = line.split(/\*\*(.*?)\*\*/g);
        return <p key={i} className="leading-relaxed">{parts.map((p, j) => j % 2 === 1 ? <strong key={j}>{p}</strong> : p)}</p>;
      })}
    </div>
  );
};

// Lucide-Icons statt Emoji (Emoji können auf Android als Fragezeichen erscheinen)
const CATEGORY_ICONS = {
  'Temperatur': Thermometer,
  'Unterweisungen': ClipboardList,
  'Bestellungen': ShoppingCart,
  'Lieferungen': Package,
  'Wegfahrer': Fuel,
  'Kassensturz': BarChart3,
  'Personal': User,
  'Aufgaben': CheckSquare,
  'MHD-Kontrolle': Calendar,
  'Abscheider': Wrench,
  'Konto & Abonnement': CreditCard,
  'Erste Schritte': Zap,
};

export const HelpCenter = () => {
  const [searchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', text: 'Hallo! Ich bin dein AllFlowX-Assistent. Stell mir eine Frage – ich helfe dir sofort weiter!' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Support-Formular
  const [showSupportForm, setShowSupportForm] = useState(false);
  const [supportForm, setSupportForm] = useState({ subject: '', description: '', urgency: 'stoerend' });
  const [supportSending, setSupportSending] = useState(false);

  // Meine Anfragen
  const [activeTab, setActiveTab] = useState('articles'); // 'articles' | 'meine-anfragen'
  const [myTickets, setMyTickets] = useState([]);
  const [myTicketsLoading, setMyTicketsLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // URL-Parameter: ?tab=meine-anfragen öffnet direkt den Anfragen-Tab
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'meine-anfragen') {
      setActiveTab('meine-anfragen');
    }
  }, [searchParams]);

  const STATUS_LABELS = { open: 'Offen', in_progress: 'In Bearbeitung', answered: 'Beantwortet', closed: 'Geschlossen' };
  const STATUS_COLORS = { open: 'bg-red-100 text-red-800', in_progress: 'bg-yellow-100 text-yellow-800', answered: 'bg-blue-100 text-blue-800', closed: 'bg-gray-100 text-gray-600' };

  const loadMyTickets = useCallback(async () => {
    setMyTicketsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/support/my-tickets`, { headers: { Authorization: `Bearer ${token}` } });
      setMyTickets(res.data.tickets || []);
      // Antworten als gelesen markieren
      for (const t of (res.data.tickets || [])) {
        if (t.unread_reply_count > 0) {
          await axios.post(`${API}/support/tickets/${t.id}/mark-read`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
        }
      }
    } catch {}
    finally { setMyTicketsLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'meine-anfragen') loadMyTickets();
  }, [activeTab, loadMyTickets]);

  const handleSupportSubmit = async () => {
    if (!supportForm.description.trim()) { toast.error('Bitte beschreibe dein Anliegen'); return; }
    setSupportSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/support/contact`, {
        ...supportForm,
        page: window.location.pathname,
        user_agent: navigator.userAgent,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Ticket ${res.data.ticket_ref} erstellt — Bestätigung per E-Mail`);
      setShowSupportForm(false);
      setSupportForm({ subject: '', description: '', urgency: 'stoerend' });
      setActiveTab('meine-anfragen');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Senden');
    } finally { setSupportSending(false); }
  };

  useEffect(() => {
    axios.get(`${API}/help/articles`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }).then(r => setArticles(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const filtered = articles.filter(a =>
    !search.trim() ||
    a.title?.toLowerCase().includes(search.toLowerCase()) ||
    a.summary?.toLowerCase().includes(search.toLowerCase()) ||
    a.category?.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = filtered.reduce((acc, a) => {
    if (!acc[a.category]) acc[a.category] = [];
    acc[a.category].push(a);
    return acc;
  }, {});

  const sendChat = async () => {
    const q = chatInput.trim();
    if (!q || chatLoading) return;
    setChatMessages(m => [...m, { role: 'user', text: q }]);
    setChatInput('');
    setChatLoading(true);
    try {
      const res = await axios.post(`${API}/help/chat`, { question: q },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setChatMessages(m => [...m, { role: 'assistant', text: res.data.answer }]);
    } catch {
      setChatMessages(m => [...m, { role: 'assistant', text: 'Fehler. Schreibe uns: **support@allflowx.de**' }]);
    } finally { setChatLoading(false); }
  };

  if (selectedArticle) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto space-y-4 pb-8">
          <Button variant="ghost" onClick={() => setSelectedArticle(null)} className="gap-2 -ml-2">
            <ChevronLeft className="h-4 w-4" /> Zurück
          </Button>
          <div>
            <Badge variant="secondary" className="mb-2 flex items-center gap-1 w-fit">
              {(() => { const Icon = CATEGORY_ICONS[selectedArticle.category] || BookOpen; return <Icon className="h-3 w-3" />; })()}
              {selectedArticle.category}
            </Badge>
            <h1 className="text-xl font-bold">{selectedArticle.title}</h1>
            {selectedArticle.summary && <p className="text-muted-foreground text-sm mt-1">{selectedArticle.summary}</p>}
          </div>
          <Card><CardContent className="pt-5"><MarkdownContent content={selectedArticle.content} /></CardContent></Card>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <Mail className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">Noch Fragen?</p>
              <a href="mailto:support@allflowx.de" className="text-xs font-bold text-blue-600 underline">support@allflowx.de</a>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-5 pb-8">
        <div className="text-center space-y-1 pt-2">
          <h1 className="text-2xl font-bold flex items-center justify-center gap-2">
            <BookOpen className="h-6 w-6 text-primary" /> Hilfe-Center
          </h1>
          <p className="text-muted-foreground text-sm">Anleitungen für alle Funktionen von AllFlowX</p>
        </div>

        {/* Tab-Auswahl */}
        <div className="flex gap-1 bg-muted rounded-xl p-1">
          <button onClick={() => setActiveTab('articles')} className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-colors ${activeTab==='articles' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-articles">
            Anleitungen
          </button>
          <button onClick={() => setActiveTab('meine-anfragen')} className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5 ${activeTab==='meine-anfragen' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-meine-anfragen">
            <Ticket className="h-3.5 w-3.5" />
            Meine Anfragen
            {myTickets.some(t => t.unread_reply_count > 0) && (
              <span className="h-2 w-2 rounded-full bg-red-500" />
            )}
          </button>
        </div>

        {/* ── Tab: Anleitungen ── */}
        {activeTab === 'articles' && (<>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Suche nach Anleitungen... (z.B. Temperatur messen)" className="pl-9 h-11"
            value={search} onChange={e => setSearch(e.target.value)} style={{ fontSize: '16px' }} data-testid="help-search" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => setShowChat(true)} className="flex items-center gap-3 p-4 bg-primary/5 border border-primary/20 rounded-xl hover:bg-primary/10 transition-colors text-left" data-testid="help-chat-btn">
            <MessageCircle className="h-5 w-5 text-primary shrink-0" />
            <div><p className="font-semibold text-sm">KI-Assistent</p><p className="text-xs text-muted-foreground">Sofortige Antworten</p></div>
          </button>
          {/* Support-Kachel → öffnet echtes Ticket-Formular */}
          <button onClick={() => setShowSupportForm(true)} className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-colors text-left" data-testid="help-support-btn">
            <Ticket className="h-5 w-5 text-blue-600 shrink-0" />
            <div>
              <p className="font-semibold text-sm text-blue-800 dark:text-blue-300">Support anfragen</p>
              <p className="text-xs text-blue-600">Ticket erstellen</p>
            </div>
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <HelpCircle className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p>Keine Artikel für &quot;{search}&quot;</p>
            <Button variant="link" onClick={() => setSearch('')}>Zurücksetzen</Button>
          </div>
        ) : Object.entries(grouped).map(([cat, arts]) => (
          <div key={cat} className="space-y-2">
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              {(() => { const Icon = CATEGORY_ICONS[cat] || BookOpen; return <Icon className="h-3.5 w-3.5" />; })()}
              {cat}
            </h2>
            <div className="space-y-1.5">
              {arts.map(a => (
                <button key={a.id} onClick={() => setSelectedArticle(a)}
                  className="w-full flex items-center justify-between p-3 bg-card border border-border rounded-xl hover:border-primary/40 hover:bg-muted/30 transition-all text-left group" data-testid={`article-${a.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-sm group-hover:text-primary">{a.title}</p>
                    {a.summary && <p className="text-xs text-muted-foreground truncate mt-0.5">{a.summary}</p>}
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 ml-2 group-hover:text-primary" />
                </button>
              ))}
            </div>
          </div>
        ))}

        <div className="text-center py-4 border-t border-border">
          <p className="text-xs text-muted-foreground">Nicht gefunden?{' '}
            <button onClick={() => setShowSupportForm(true)} className="text-primary font-medium hover:underline">Support anfragen</button>
          </p>
        </div>
        </>)}

        {/* ── Tab: Meine Anfragen ── */}
        {activeTab === 'meine-anfragen' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{myTickets.length} Ticket{myTickets.length !== 1 ? 's' : ''}</p>
              <Button size="sm" variant="outline" onClick={() => setShowSupportForm(true)} className="gap-1.5" data-testid="new-ticket-btn">
                <Ticket className="h-3.5 w-3.5" /> Neue Anfrage
              </Button>
            </div>

            {myTicketsLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground"/></div>
            ) : myTickets.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground space-y-3">
                <Ticket className="h-10 w-10 mx-auto opacity-25"/>
                <p className="text-sm">Noch keine Anfragen</p>
                <Button size="sm" onClick={() => setShowSupportForm(true)}>Erste Anfrage stellen</Button>
              </div>
            ) : selectedTicket ? (
              <div className="space-y-3">
                <button onClick={() => setSelectedTicket(null)} className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground">
                  <ChevronLeft className="h-3.5 w-3.5"/> Alle Tickets
                </button>
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="bg-muted/30 p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-primary">#{selectedTicket.ticket_number}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[selectedTicket.status] || ''}`}>{STATUS_LABELS[selectedTicket.status] || selectedTicket.status}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{new Date(selectedTicket.created_at).toLocaleString('de-DE')}</p>
                  </div>
                  <div className="p-4 space-y-4">
                    <div className="bg-muted/20 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Ihre Anfrage:</p>
                      <p className="text-sm whitespace-pre-wrap">{selectedTicket.description}</p>
                    </div>
                    {(selectedTicket.replies || []).filter(r => r.is_customer_visible).map(r => (
                      <div key={r.id} className="bg-primary/5 border border-primary/20 rounded-lg p-3 ml-4">
                        <div className="flex items-center gap-1.5 mb-1">
                          <CheckCircle2 className="h-3.5 w-3.5 text-primary"/>
                          <span className="text-xs font-semibold text-primary">{r.author}</span>
                          <span className="text-[10px] text-muted-foreground">{new Date(r.timestamp).toLocaleString('de-DE')}</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{r.text}</p>
                      </div>
                    ))}
                    {(selectedTicket.replies || []).filter(r => r.is_customer_visible).length === 0 && (
                      <div className="text-center py-4 text-xs text-muted-foreground flex items-center justify-center gap-1.5">
                        <Clock className="h-3.5 w-3.5"/> Wir melden uns bald. Bestätigung per E-Mail.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              myTickets.map(t => (
                <button key={t.id} onClick={() => setSelectedTicket(t)}
                  className="w-full text-left rounded-xl border border-border p-4 hover:border-primary/40 hover:bg-muted/20 transition-colors"
                  data-testid={`my-ticket-${t.ticket_number}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-bold text-primary">#{t.ticket_number}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[t.status] || ''}`}>{STATUS_LABELS[t.status] || t.status}</span>
                        {t.unread_reply_count > 0 && (
                          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white">{t.unread_reply_count} neu</span>
                        )}
                      </div>
                      <p className="text-sm text-foreground truncate">{t.description?.slice(0,80)}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{new Date(t.created_at).toLocaleDateString('de-DE')} · {t.replies?.length || 0} Antwort{t.replies?.length !== 1 ? 'en' : ''}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5"/>
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* ── Support-Ticket-Formular Modal ── */}
      {showSupportForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-background w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Ticket className="h-4 w-4 text-primary"/>
                <span className="font-semibold text-sm">Support anfragen</span>
              </div>
              <button onClick={() => setShowSupportForm(false)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4"/></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Betreff (optional)</label>
                <Input value={supportForm.subject} onChange={e => setSupportForm(f => ({...f, subject: e.target.value}))}
                  placeholder="Kurze Beschreibung Ihres Anliegens" className="h-9 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Beschreibung *</label>
                <textarea value={supportForm.description} onChange={e => setSupportForm(f => ({...f, description: e.target.value}))}
                  placeholder="Beschreiben Sie Ihr Anliegen möglichst genau..."
                  rows={4} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30" data-testid="support-form-description" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Dringlichkeit</label>
                <select value={supportForm.urgency} onChange={e => setSupportForm(f => ({...f, urgency: e.target.value}))}
                  className="w-full h-9 rounded-lg border border-border bg-background px-3 text-sm">
                  <option value="kritisch">🔴 Kritisch — App nicht nutzbar</option>
                  <option value="stoerend">🟡 Störend — Feature funktioniert nicht richtig</option>
                  <option value="kleinigkeit">🟢 Kleinigkeit — Verbesserungsvorschlag</option>
                </select>
              </div>
              <Button onClick={handleSupportSubmit} disabled={!supportForm.description.trim() || supportSending} className="w-full gap-1.5" data-testid="support-form-submit">
                {supportSending ? <Loader2 className="h-4 w-4 animate-spin"/> : <Send className="h-4 w-4"/>}
                Ticket erstellen
              </Button>
              <p className="text-[10px] text-muted-foreground text-center">Sie erhalten eine Bestätigungs-E-Mail mit Ihrer Ticketnummer.</p>
            </div>
          </div>
        </div>
      )}

      {/* AI Chat */}
      {showChat && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <div className="bg-white dark:bg-gray-900 w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl flex flex-col" style={{ maxHeight: '85dvh', minHeight: '380px' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-primary" />
                <span className="font-semibold text-sm">AllFlowX Assistent</span>
                <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">KI</span>
              </div>
              <button onClick={() => setShowChat(false)} className="p-1 rounded-full hover:bg-muted"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chatMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>
                    <MarkdownContent content={msg.text} />
                  </div>
                </div>
              ))}
              {chatLoading && <div className="flex justify-start"><div className="bg-muted px-3 py-2 rounded-xl"><Loader2 className="h-4 w-4 animate-spin" /></div></div>}
              <div ref={chatEndRef} />
            </div>
            <div className="p-3 border-t border-border shrink-0">
              <div className="flex gap-2">
                <Input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendChat()}
                  placeholder="Frage stellen..." style={{ fontSize: '16px' }} disabled={chatLoading} data-testid="chat-input" />
                <Button onClick={sendChat} disabled={!chatInput.trim() || chatLoading} size="sm" data-testid="chat-send"><Send className="h-4 w-4" /></Button>
              </div>
              <p className="text-center text-xs text-muted-foreground mt-2">
                Nicht weitergekommen?{' '}
                <a href="mailto:support@allflowx.de" className="text-primary font-medium">support@allflowx.de</a>
              </p>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default HelpCenter;
